import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { ApiResponse } from '@mytag/shared';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { getOnlineStatus } from '@/lib/connectivity';
import { isNativeAndroid } from '@/lib/nativeRuntime';

const SESSION_KEY = 'mytag.sessionId';
const NATIVE_EXPORT_DIRECTORY = 'mytag-exports';
const MAX_NATIVE_EXPORT_BYTES = 15 * 1024 * 1024;

export interface DownloadResult {
  filename: string;
  delivery: 'browser-download' | 'android-share';
}

/**
 * Client HTTP centralisé.
 * Le navigateur ne parle QU'AU backend MyTag : aucune URL Vernal, aucun CID,
 * aucune clé de chiffrement ne transite ni n'est stockée côté client.
 */
const baseURL = import.meta.env.VITE_API_BASE_URL?.trim() || '/api';

export const sessionStorageKey = SESSION_KEY;

export function getSessionId(): string | null {
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function setSessionId(sessionId: string | null): void {
  try {
    if (sessionId) window.localStorage.setItem(SESSION_KEY, sessionId);
    else window.localStorage.removeItem(SESSION_KEY);
  } catch {
    /* stockage indisponible (navigation privée) : la session reste en mémoire */
  }
}

/** Erreur normalisée exploitable par l'UI (message en français + diagnostic). */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  readonly hint?: string;
  readonly endpoint?: string;
  readonly requestId?: string;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    details?: unknown;
    hint?: string;
    endpoint?: string;
    requestId?: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.hint = init.hint;
    this.endpoint = init.endpoint;
    this.requestId = init.requestId;
  }

  get isAuthError(): boolean {
    return this.status === 401 || this.code === 'UNAUTHORIZED' || this.code === 'SESSION_EXPIRED';
  }
}

const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase() ?? 'GET';
  if (!getOnlineStatus() && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    window.dispatchEvent(new Event('mytag:offline-action-blocked'));
    return Promise.reject(new ApiError({
      code: 'OFFLINE_ACTION_DISABLED',
      message: 'Action indisponible hors connexion.',
      status: 0,
      endpoint: `${method} ${config.url ?? baseURL}`,
      hint: 'Reconnectez-vous : cette action ne sera pas mise en attente.',
    }));
  }
  const sessionId = getSessionId();
  if (sessionId) config.headers.Authorization = `Bearer ${sessionId}`;
  return config;
});

function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  const axiosError = error as AxiosError<ApiResponse<unknown>>;
  const payload = axiosError.response?.data;
  const localEndpoint = `${axiosError.config?.method?.toUpperCase() ?? 'HTTP'} ${axiosError.config?.url ?? baseURL}`;

  if (payload && typeof payload === 'object' && 'ok' in payload && payload.ok === false) {
    const backendError = payload.error as typeof payload.error & { requestId?: string };
    return new ApiError({
      code: backendError.code,
      message: backendError.message,
      status: axiosError.response?.status ?? 500,
      details: backendError.details,
      hint: backendError.hint,
      endpoint: backendError.endpoint,
      requestId: backendError.requestId ?? axiosError.response?.headers['x-request-id'],
    });
  }

  if (axiosError.code === 'ECONNABORTED') {
    return new ApiError({
      code: 'CLIENT_TIMEOUT',
      message: "Le backend MyTag n'a pas répondu à temps.",
      status: 504,
      endpoint: localEndpoint,
      hint: 'Vérifier que l\'API tourne sur http://localhost:4000 (npm run dev:api).',
    });
  }

  if (!axiosError.response) {
    return new ApiError({
      code: 'NETWORK_ERROR',
      message: 'Backend MyTag injoignable.',
      status: 0,
      endpoint: localEndpoint,
      hint: 'Démarrer l\'API : npm run dev:api (port 4000).',
    });
  }

  return new ApiError({
    code: 'HTTP_ERROR',
    message: `Erreur HTTP ${axiosError.response.status}.`,
    status: axiosError.response.status,
    endpoint: localEndpoint,
    requestId: axiosError.response.headers['x-request-id'],
  });
}

async function toDownloadApiError(error: unknown): Promise<ApiError> {
  if (error instanceof ApiError) return error;

  if (error instanceof Error && !axios.isAxiosError(error)) {
    return new ApiError({
      code: 'NATIVE_EXPORT_FAILED',
      message: 'Le fichier a été reçu mais sa remise à Android a échoué.',
      status: 0,
      hint: 'Réessayez puis choisissez une application capable d’enregistrer ou de partager ce format.',
    });
  }

  const axiosError = error as AxiosError<Blob>;
  const payload = axiosError.response?.data;
  if (payload instanceof Blob && payload.size <= 64 * 1024) {
    try {
      const parsed = JSON.parse(await payload.text()) as ApiResponse<unknown>;
      if (parsed && typeof parsed === 'object' && parsed.ok === false) {
        const backendError = parsed.error as typeof parsed.error & { requestId?: string };
        return new ApiError({
          code: backendError.code,
          message: backendError.message,
          status: axiosError.response?.status ?? 500,
          details: backendError.details,
          hint: backendError.hint,
          endpoint: backendError.endpoint ?? `${axiosError.config?.method?.toUpperCase() ?? 'HTTP'} ${axiosError.config?.url ?? baseURL}`,
          requestId: backendError.requestId ?? axiosError.response?.headers['x-request-id'],
        });
      }
    } catch {
      // Une réponse binaire ou non JSON est traitée par le diagnostic HTTP générique.
    }
  }
  return toApiError(error);
}

function filenameFromDisposition(disposition: unknown, fallback: string): string {
  const value = String(disposition ?? '');
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try { return decodeURIComponent(encoded); } catch { /* utiliser le format simple */ }
  }
  return value.match(/filename="([^"]+)"/i)?.[1] ?? fallback;
}

function safeFilename(filename: string): string {
  const withoutControls = [...filename.normalize('NFKC')]
    .map((character) => character.charCodeAt(0) < 32 ? '_' : character)
    .join('');
  const normalized = withoutControls
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/^\.+/, '')
    .slice(0, 160)
    .trim();
  return normalized || 'mytag-export';
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Lecture du fichier exporté impossible.'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const separator = result.indexOf(',');
      if (separator < 0) reject(new Error('Encodage du fichier exporté impossible.'));
      else resolve(result.slice(separator + 1));
    };
    reader.readAsDataURL(blob);
  });
}

async function cleanupOldNativeExports(): Promise<void> {
  try {
    const { files } = await Filesystem.readdir({ path: NATIVE_EXPORT_DIRECTORY, directory: Directory.Cache });
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    await Promise.all(files
      .filter((file) => file.type === 'file' && file.mtime < cutoff)
      .map((file) => Filesystem.deleteFile({
        path: `${NATIVE_EXPORT_DIRECTORY}/${file.name}`,
        directory: Directory.Cache,
      }).catch(() => undefined)));
  } catch {
    // Le répertoire n'existe pas encore ou le cache a déjà été nettoyé par Android.
  }
}

async function deliverBlob(blob: Blob, unsafeFilename: string): Promise<DownloadResult> {
  const filename = safeFilename(unsafeFilename);

  if (!isNativeAndroid()) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    return { filename, delivery: 'browser-download' };
  }

  if (blob.size > MAX_NATIVE_EXPORT_BYTES) {
    throw new ApiError({
      code: 'NATIVE_EXPORT_TOO_LARGE',
      message: 'Le fichier dépasse la limite mobile de 15 Mo.',
      status: 413,
      hint: 'Réduisez la période exportée puis réessayez.',
    });
  }

  await cleanupOldNativeExports();
  const path = `${NATIVE_EXPORT_DIRECTORY}/${Date.now()}-${filename}`;
  const data = await blobToBase64(blob);
  const write = await Filesystem.writeFile({ path, data, directory: Directory.Cache, recursive: true });
  const canShare = await Share.canShare();
  if (!canShare.value) {
    await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined);
    throw new ApiError({
      code: 'NATIVE_SHARE_UNAVAILABLE',
      message: 'Le partage de fichiers Android est indisponible sur cet appareil.',
      status: 0,
    });
  }

  await Share.share({
    title: filename,
    text: 'Export MyTag',
    files: [write.uri],
    dialogTitle: 'Enregistrer ou partager l’export MyTag',
  });

  // Le fichier reste brièvement disponible pour l'application destinataire, puis est supprimé.
  window.setTimeout(() => {
    void Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined);
  }, 5 * 60 * 1000);
  return { filename, delivery: 'android-share' };
}

/** Déballe l'enveloppe { ok, data } du backend. */
async function unwrap<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  try {
    const response = await promise;
    if (!response.data.ok) {
      throw new ApiError({
        code: response.data.error.code,
        message: response.data.error.message,
        status: 400,
        details: response.data.error.details,
        hint: response.data.error.hint,
        endpoint: response.data.error.endpoint,
      });
    }
    return response.data.data;
  } catch (error) {
    throw toApiError(error);
  }
}

export const apiClient = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    unwrap<T>(http.get<ApiResponse<T>>(url, { params })),
  post: <T>(url: string, body?: unknown) => unwrap<T>(http.post<ApiResponse<T>>(url, body)),
  patch: <T>(url: string, body?: unknown) => unwrap<T>(http.patch<ApiResponse<T>>(url, body)),
  delete: <T>(url: string) => unwrap<T>(http.delete<ApiResponse<T>>(url)),
  async download(url: string, params?: Record<string, unknown>): Promise<void> {
    try {
      if (!getOnlineStatus()) {
        window.dispatchEvent(new Event('mytag:offline-action-blocked'));
        throw new ApiError({ code: 'OFFLINE_ACTION_DISABLED', message: 'Téléchargement indisponible hors connexion.', status: 0, endpoint: `GET ${url}`, hint: 'Reconnectez-vous puis relancez le téléchargement.' });
      }
      const response = await http.get<Blob>(url, { params, responseType: 'blob' });
      const filename = filenameFromDisposition(response.headers['content-disposition'], 'mytag-export');
      await deliverBlob(response.data, filename);
    } catch (error) { throw await toDownloadApiError(error); }
  },
  async downloadPost(url: string, body: unknown): Promise<DownloadResult> {
    try {
      if (!getOnlineStatus()) {
        window.dispatchEvent(new Event('mytag:offline-action-blocked'));
        throw new ApiError({ code: 'OFFLINE_ACTION_DISABLED', message: 'Téléchargement indisponible hors connexion.', status: 0, endpoint: `POST ${url}`, hint: 'Reconnectez-vous puis relancez le téléchargement.' });
      }
      const response = await http.post<Blob>(url, body, { responseType: 'blob' });
      const filename = filenameFromDisposition(response.headers['content-disposition'], 'MyTag_Rapport_CDC.docx');
      return await deliverBlob(response.data, filename);
    } catch (error) { throw await toDownloadApiError(error); }
  },
  baseURL,
};
